import React, { Suspense } from "react";
import { Switch, Route, useLocation } from "wouter";
import { queryClient } from "./lib/queryClient";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { ThemeProvider } from "@/components/theme-provider";
import { BrandProvider, useBrand } from "@/lib/brand-context";
import { AuthGate } from "@/lib/auth-machine";
import { PersonalizationProvider } from "@/lib/personalization-context";
import { getIsSatelliteDomain, getIsNativePlatform } from "@/lib/domain-utils";
import { isNativeApp } from "@/lib/capacitor";
import { StaffProvider } from "@/lib/staff-context";
import { SiteProvider, useSite } from "@/lib/site-context";
import { AppResumeHandler } from "@/lib/app-resume-handler";
import { PushNotificationManager } from "@/lib/push-notification-manager";
import { ForegroundNotificationBanner } from "@/components/foreground-notification-banner";
import { ConnectionRecoveryProvider } from "@/lib/connection-recovery";
import { ConnectionRecoveryModal } from "@/components/connection-recovery-modal";
import { MaintenanceGuard } from "@/components/staff/MaintenanceGuard";
import { ConvexCacheProvider } from "@/lib/convex-cache-provider";
import { DevBar } from "@/components/dev-bar";
import { AuthTransitionLoader } from "@/components/auth-transition-loader";
import { ChunkPrefetcher } from "@/lib/chunk-prefetcher";
import NotFound from "@/pages/not-found";
import { AppLayout } from "@/components/app-layout";
import { AIChatWidget } from "@/components/ai-chat-widget";
import { useSessionId } from "@/lib/auth-machine";

const isDev = import.meta.env.DEV;

// Lazy-loaded page components — keeps the main bundle small and avoids
// module initialization ordering issues (TDZ errors) in the bundler.
const Landing = React.lazy(() => import("@/pages/landing"));
const Pricing = React.lazy(() => import("@/pages/pricing"));
const About = React.lazy(() => import("@/pages/about"));
const Contact = React.lazy(() => import("@/pages/contact"));
const Integrations = React.lazy(() => import("@/pages/integrations"));
const POSProduct = React.lazy(() => import("@/pages/products/pos"));
const RestaurantProduct = React.lazy(() => import("@/pages/products/restaurant"));
const RetailProduct = React.lazy(() => import("@/pages/products/retail"));
const RosteringProduct = React.lazy(() => import("@/pages/products/rostering"));
const WebsiteProduct = React.lazy(() => import("@/pages/products/website"));
const Privacy = React.lazy(() => import("@/pages/legal/privacy"));
const Terms = React.lazy(() => import("@/pages/legal/terms"));
const Security = React.lazy(() => import("@/pages/legal/security"));
const Help = React.lazy(() => import("@/pages/resources/help"));
const Blog = React.lazy(() => import("@/pages/resources/blog"));
const ModifiersFeature = React.lazy(() => import("@/pages/features/modifiers"));
const CategoriesFeature = React.lazy(() => import("@/pages/features/categories"));
const LayoutFeature = React.lazy(() => import("@/pages/features/layout"));
const OrderingFeature = React.lazy(() => import("@/pages/features/ordering"));
const StaffFeature = React.lazy(() => import("@/pages/features/staff"));
const AnalyticsFeature = React.lazy(() => import("@/pages/features/analytics"));

const ShiftCanvasLanding = React.lazy(() => import("@/pages/shiftcanvas/landing"));
const ShiftCanvasPricing = React.lazy(() => import("@/pages/shiftcanvas/pricing"));
const ShiftCanvasAbout = React.lazy(() => import("@/pages/shiftcanvas/about"));
const ShiftCanvasContact = React.lazy(() => import("@/pages/shiftcanvas/contact"));
const ShiftCanvasFeatures = React.lazy(() => import("@/pages/shiftcanvas/features"));
const ShiftCanvasPrivacy = React.lazy(() => import("@/pages/shiftcanvas/privacy"));
const ShiftCanvasTerms = React.lazy(() => import("@/pages/shiftcanvas/terms"));

const AppDashboard = React.lazy(() => import("@/pages/app/dashboard"));
const AppRoster = React.lazy(() => import("@/pages/app/roster"));
const AppMyRoster = React.lazy(() => import("@/pages/app/my-roster"));
const AppRosterCreate = React.lazy(() => import("@/pages/app/roster-create"));
const AppStaff = React.lazy(() => import("@/pages/app/staff"));
const AppAvailability = React.lazy(() => import("@/pages/app/availability"));
const AppRequests = React.lazy(() => import("@/pages/app/requests"));
const AppProfile = React.lazy(() => import("@/pages/app/profile"));
const AppNotifications = React.lazy(() => import("@/pages/app/notifications"));
const AppClock = React.lazy(() => import("@/pages/app/clock"));
const AppClockIn = React.lazy(() => import("@/pages/app/clock-in"));
const AppJoin = React.lazy(() => import("@/pages/app/join"));
const AppLogin = React.lazy(() => import("@/pages/app/login"));
const NativeLoginPage = React.lazy(() => import("@/pages/app/native-login"));
const AppSSOCallback = React.lazy(() => import("@/pages/app/sso-callback"));
const AppOAuthComplete = React.lazy(() => import("@/pages/app/oauth-complete"));
const AppMessages = React.lazy(() => import("@/pages/app/messages"));
const AppFeedback = React.lazy(() => import("@/pages/app/feedback"));
const AppCalendar = React.lazy(() => import("@/pages/app/calendar"));
const AppMeetings = React.lazy(() => import("@/pages/app/meetings"));
const AppProjects = React.lazy(() => import("@/pages/app/projects"));
const AppReminders = React.lazy(() => import("@/pages/app/reminders"));
const AppTasks = React.lazy(() => import("@/pages/app/tasks"));
const AppTraining = React.lazy(() => import("@/pages/app/training"));
const AppProcedures = React.lazy(() => import("@/pages/app/procedures"));
const AppProjectTask = React.lazy(() => import("@/pages/app/project-task"));
const Onboarding = React.lazy(() => import("@/pages/onboarding"));
const FAQ = React.lazy(() => import("@/pages/faq"));
const Guides = React.lazy(() => import("@/pages/guides"));
const Support = React.lazy(() => import("@/pages/support"));
const DeleteAccount = React.lazy(() => import("@/pages/delete-account"));
const TestUploadPage = React.lazy(() => import("@/pages/test/upload"));

const StaffDashboard = React.lazy(() => import("@/pages/staff/StaffDashboard"));
const StaffRoster = React.lazy(() => import("@/pages/staff/StaffRoster"));
const StaffSchedule = React.lazy(() => import("@/pages/staff/StaffSchedule"));
const StaffClock = React.lazy(() => import("@/pages/staff/StaffClock"));
const StaffMessages = React.lazy(() => import("@/pages/staff/StaffMessages"));
const StaffProfile = React.lazy(() => import("@/pages/staff/StaffProfile"));
const StaffSettings = React.lazy(() => import("@/pages/staff/StaffSettings"));
const StaffTraining = React.lazy(() => import("@/pages/staff/StaffTraining"));
const StaffProcedures = React.lazy(() => import("@/pages/staff/StaffProcedures"));
const StaffTasks = React.lazy(() => import("@/pages/staff/StaffTasks"));
const StaffProjects = React.lazy(() => import("@/pages/staff/StaffProjects"));
const StaffCalendar = React.lazy(() => import("@/pages/staff/StaffCalendar"));
const StaffMeetings = React.lazy(() => import("@/pages/staff/StaffMeetings"));
const StaffTimesheet = React.lazy(() => import("@/pages/staff/StaffTimesheet"));
const StaffConfiguration = React.lazy(() => import("@/pages/staff/StaffConfiguration"));
const StaffAdminRoster = React.lazy(() => import("@/pages/staff/admin/StaffAdminRoster"));
const StaffAdminStaff = React.lazy(() => import("@/pages/staff/admin/StaffAdminStaff"));
const StaffAdminAvailability = React.lazy(() => import("@/pages/staff/admin/StaffAdminAvailability"));
const StaffAdminLeave = React.lazy(() => import("@/pages/staff/admin/StaffAdminLeave"));
const StaffAdminShiftSwaps = React.lazy(() => import("@/pages/staff/admin/StaffAdminShiftSwaps"));
const StaffAdminRequests = React.lazy(() => import("@/pages/staff/admin/StaffAdminRequests"));
const StaffAdminReports = React.lazy(() => import("@/pages/staff/admin/StaffAdminReports"));
const StaffMeetingView = React.lazy(() => import("@/pages/staff/StaffMeetingView"));
const StaffAdminDetail = React.lazy(() => import("@/pages/staff/admin/StaffAdminDetail"));

// A signed-in user with zero site memberships must land on onboarding
// (join / create a workplace), never on an empty dashboard. This happens for
// brand-new accounts — including a duplicate Kinde user created by signing in
// with a different provider for an email whose identities aren't linked.
function RequireSiteMembership({ children }: { children: React.ReactNode }) {
  const { sites, isLoading } = useSite();
  const [location, setLocation] = useLocation();
  const noSites = !isLoading && sites.length === 0;

  React.useEffect(() => {
    if (noSites && location !== "/onboarding") {
      console.log("[App] Signed in with no site memberships — redirecting to onboarding");
      setLocation("/onboarding");
    }
  }, [noSites, location, setLocation]);

  if (noSites) return null;
  return <>{children}</>;
}

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const sessionId = useSessionId();

  return (
    <AuthGate>
      <div key={sessionId ?? "loading"}>
        <ConnectionRecoveryProvider>
          <SiteProvider>
            <StaffProvider>
              <ConvexCacheProvider>
                <AppResumeHandler />
                <ChunkPrefetcher />
                <PushNotificationManager>
                  <ForegroundNotificationBanner />
                  <ConnectionRecoveryModal />
                  <MaintenanceGuard>
                    <RequireSiteMembership>
                      <AppLayout>{children}</AppLayout>
                    </RequireSiteMembership>
                  </MaintenanceGuard>
                </PushNotificationManager>
              </ConvexCacheProvider>
            </StaffProvider>
          </SiteProvider>
        </ConnectionRecoveryProvider>
      </div>
    </AuthGate>
  );
}

function ProtectedAppShell({ children }: { children: React.ReactNode }) {
  const sessionId = useSessionId();

  return (
    <AuthGate>
      <div key={sessionId ?? "loading"}>
        <ConnectionRecoveryProvider>
          <SiteProvider>
            <StaffProvider>
              <ConvexCacheProvider>
                <AppResumeHandler />
                <ChunkPrefetcher />
                <PushNotificationManager>
                  <ForegroundNotificationBanner />
                  <ConnectionRecoveryModal />
                  <MaintenanceGuard>
                    <RequireSiteMembership>
                      <AppLayout>{children}</AppLayout>
                    </RequireSiteMembership>
                  </MaintenanceGuard>
                </PushNotificationManager>
              </ConvexCacheProvider>
            </StaffProvider>
          </SiteProvider>
        </ConnectionRecoveryProvider>
      </div>
    </AuthGate>
  );
}

function StaffPortalRoute({ children }: { children: React.ReactNode }) {
  const sessionId = useSessionId();
  const isNative = isNativeApp();

  const content = isNative ? <AppLayout>{children}</AppLayout> : children;

  return (
    <AuthGate>
      <div key={sessionId ?? "loading"}>
        <ConnectionRecoveryProvider>
          <SiteProvider>
            <StaffProvider>
              <ConvexCacheProvider>
                <AppResumeHandler />
                <ChunkPrefetcher />
                <PushNotificationManager>
                  <ForegroundNotificationBanner />
                  <ConnectionRecoveryModal />
                  <MaintenanceGuard>
                    {/* The server's post-login redirect lands on /staff, so this
                        route needs the zero-membership gate too — without it a
                        brand-new user walks straight into an empty portal. */}
                    <RequireSiteMembership>
                      {content}
                    </RequireSiteMembership>
                  </MaintenanceGuard>
                </PushNotificationManager>
              </ConvexCacheProvider>
            </StaffProvider>
          </SiteProvider>
        </ConnectionRecoveryProvider>
      </div>
    </AuthGate>
  );
}

const ProtectedDashboard = () => <ProtectedRoute><AppDashboard /></ProtectedRoute>;
const ProtectedRoster = () => <ProtectedRoute><AppRoster /></ProtectedRoute>;
const ProtectedMyRoster = () => <ProtectedRoute><AppMyRoster /></ProtectedRoute>;
const ProtectedRosterCreate = () => <ProtectedRoute><AppRosterCreate /></ProtectedRoute>;
const ProtectedStaff = () => <ProtectedRoute><AppStaff /></ProtectedRoute>;
const ProtectedAvailability = () => <ProtectedRoute><AppAvailability /></ProtectedRoute>;
const ProtectedRequests = () => <ProtectedRoute><AppRequests /></ProtectedRoute>;
const ProtectedProfile = () => <ProtectedRoute><AppProfile /></ProtectedRoute>;
const ProtectedNotifications = () => <ProtectedRoute><AppNotifications /></ProtectedRoute>;
const ProtectedClock = () => <ProtectedRoute><AppClock /></ProtectedRoute>;
const ProtectedMessages = () => <ProtectedRoute><AppMessages /></ProtectedRoute>;
const ProtectedFeedback = () => <ProtectedRoute><AppFeedback /></ProtectedRoute>;
const ProtectedCalendar = () => <ProtectedRoute><AppCalendar /></ProtectedRoute>;
const ProtectedMeetings = () => <ProtectedRoute><AppMeetings /></ProtectedRoute>;
const ProtectedProjects = () => <ProtectedRoute><AppProjects /></ProtectedRoute>;
const ProtectedReminders = () => <ProtectedRoute><AppReminders /></ProtectedRoute>;
const ProtectedTasks = () => <ProtectedRoute><AppTasks /></ProtectedRoute>;
const ProtectedTraining = () => <ProtectedRoute><AppTraining /></ProtectedRoute>;
const ProtectedProcedures = () => <ProtectedRoute><AppProcedures /></ProtectedRoute>;
const ProtectedProjectTask = () => <ProtectedRoute><AppProjectTask /></ProtectedRoute>;

const StaffPortalDashboard = () => <StaffPortalRoute><StaffDashboard /></StaffPortalRoute>;
const StaffPortalRoster = () => <StaffPortalRoute><StaffRoster /></StaffPortalRoute>;
const StaffPortalSchedule = () => <StaffPortalRoute><StaffSchedule /></StaffPortalRoute>;
const StaffPortalClock = () => <StaffPortalRoute><StaffClock /></StaffPortalRoute>;
const StaffPortalMessages = () => <StaffPortalRoute><StaffMessages /></StaffPortalRoute>;
const StaffPortalProfile = () => <StaffPortalRoute><StaffProfile /></StaffPortalRoute>;
const StaffPortalSettings = ({ initialTab }: { initialTab?: string } = {}) => <StaffPortalRoute><StaffSettings initialTab={initialTab} /></StaffPortalRoute>;
const StaffPortalTraining = () => <StaffPortalRoute><StaffTraining /></StaffPortalRoute>;
const StaffPortalProcedures = () => <StaffPortalRoute><StaffProcedures /></StaffPortalRoute>;
const StaffPortalTasks = () => <StaffPortalRoute><StaffTasks /></StaffPortalRoute>;
const StaffPortalProjects = () => <StaffPortalRoute><StaffProjects /></StaffPortalRoute>;
const StaffPortalCalendar = () => <StaffPortalRoute><StaffCalendar /></StaffPortalRoute>;
const StaffPortalMeetings = () => <StaffPortalRoute><StaffMeetings /></StaffPortalRoute>;
const StaffPortalTimesheet = () => <StaffPortalRoute><StaffTimesheet /></StaffPortalRoute>;
const StaffPortalConfiguration = ({ initialTab }: { initialTab?: string } = {}) => <StaffPortalRoute><StaffConfiguration initialTab={initialTab} /></StaffPortalRoute>;
const StaffPortalMeetingView = () => <StaffPortalRoute><StaffMeetingView /></StaffPortalRoute>;
const StaffPortalAdminRoster = () => <StaffPortalRoute><StaffAdminRoster /></StaffPortalRoute>;
const StaffPortalAdminStaff = () => <StaffPortalRoute><StaffAdminStaff /></StaffPortalRoute>;
const StaffPortalAdminAvailability = () => <StaffPortalRoute><StaffAdminAvailability /></StaffPortalRoute>;
const StaffPortalAdminRequests = () => <StaffPortalRoute><StaffAdminRequests /></StaffPortalRoute>;
const StaffPortalAdminLeave = () => <StaffPortalRoute><StaffAdminLeave /></StaffPortalRoute>;
const StaffPortalAdminShiftSwaps = () => <StaffPortalRoute><StaffAdminShiftSwaps /></StaffPortalRoute>;
const StaffPortalAdminDetail = () => <StaffPortalRoute><StaffAdminDetail /></StaffPortalRoute>;
const StaffPortalAdminReports = () => <StaffPortalRoute><StaffAdminReports /></StaffPortalRoute>;

function CentretableRouter() {
  return (
    <Switch>
      <Route path="/" component={Landing} />
      <Route path="/onboarding" component={Onboarding} />
      <Route path="/faq" component={FAQ} />
      <Route path="/guides" component={Guides} />
      <Route path="/support" component={Support} />
      <Route path="/delete-account" component={DeleteAccount} />
      <Route path="/pricing" component={Pricing} />
      <Route path="/about" component={About} />
      <Route path="/contact" component={Contact} />
      <Route path="/integrations" component={Integrations} />
      <Route path="/pos" component={POSProduct} />
      <Route path="/restaurant" component={RestaurantProduct} />
      <Route path="/retail" component={RetailProduct} />
      <Route path="/rostering" component={RosteringProduct} />
      <Route path="/website" component={WebsiteProduct} />
      <Route path="/privacy" component={Privacy} />
      <Route path="/terms" component={Terms} />
      <Route path="/security" component={Security} />
      <Route path="/help" component={Help} />
      <Route path="/blog" component={Blog} />
      <Route path="/features/modifiers" component={ModifiersFeature} />
      <Route path="/features/categories" component={CategoriesFeature} />
      <Route path="/features/layout" component={LayoutFeature} />
      <Route path="/features/ordering" component={OrderingFeature} />
      <Route path="/features/staff" component={StaffFeature} />
      <Route path="/features/analytics" component={AnalyticsFeature} />
      <Route path="/app/login" component={AppLogin} />
      <Route path="/app/sso-callback" component={AppSSOCallback} />
      <Route path="/app/oauth-complete" component={AppOAuthComplete} />
      <Route path="/admin" component={ProtectedDashboard} />
      <Route path="/app" component={ProtectedDashboard} />
      <Route path="/app/roster" component={ProtectedRoster} />
      <Route path="/app/my-roster" component={ProtectedMyRoster} />
      <Route path="/app/roster/create" component={ProtectedRosterCreate} />
      <Route path="/app/staff" component={ProtectedStaff} />
      <Route path="/app/availability" component={ProtectedAvailability} />
      <Route path="/app/requests" component={ProtectedRequests} />
      <Route path="/app/profile" component={ProtectedProfile} />
      <Route path="/app/notifications" component={ProtectedNotifications} />
      <Route path="/app/clock" component={ProtectedClock} />
      <Route path="/app/messages" component={ProtectedMessages} />
      <Route path="/app/feedback" component={ProtectedFeedback} />
      <Route path="/app/calendar" component={ProtectedCalendar} />
      <Route path="/app/meetings" component={ProtectedMeetings} />
      <Route path="/app/projects" component={ProtectedProjects} />
      <Route path="/app/project-task/:taskId" component={ProtectedProjectTask} />
      <Route path="/app/reminders" component={ProtectedReminders} />
      <Route path="/app/tasks" component={ProtectedTasks} />
      <Route path="/app/training" component={ProtectedTraining} />
      <Route path="/app/procedures" component={ProtectedProcedures} />
      <Route path="/clock/:token" component={AppClockIn} />
      <Route path="/join/:code" component={AppJoin} />
      <Route path="/staff" component={StaffPortalDashboard} />
      <Route path="/staff/dashboard" component={StaffPortalDashboard} />
      <Route path="/staff/roster" component={StaffPortalRoster} />
      <Route path="/staff/schedule" component={StaffPortalSchedule} />
      <Route path="/staff/clock" component={StaffPortalClock} />
      <Route path="/staff/messages" component={StaffPortalMessages} />
      <Route path="/staff/profile" component={StaffPortalProfile} />
      <Route path="/staff/settings">{() => <StaffPortalSettings />}</Route>
      <Route path="/staff/training" component={StaffPortalTraining} />
      <Route path="/staff/procedures" component={StaffPortalProcedures} />
      <Route path="/staff/tasks" component={StaffPortalTasks} />
      <Route path="/staff/projects" component={StaffPortalProjects} />
      <Route path="/staff/calendar" component={StaffPortalCalendar} />
      <Route path="/staff/meetings/:meetingId" component={StaffPortalMeetingView} />
      <Route path="/staff/meetings" component={StaffPortalMeetings} />
      <Route path="/staff/timesheet" component={StaffPortalTimesheet} />
      <Route path="/staff/admin/roster" component={StaffPortalAdminRoster} />
      <Route path="/staff/admin/staff" component={StaffPortalAdminStaff} />
      <Route path="/staff/admin/configuration">{() => <StaffPortalConfiguration />}</Route>
      <Route path="/staff/admin/settings">{() => <StaffPortalConfiguration initialTab="roles" />}</Route>
      <Route path="/staff/admin/roles">{() => <StaffPortalConfiguration initialTab="roles" />}</Route>
      <Route path="/staff/admin/availability" component={StaffPortalAdminAvailability} />
      <Route path="/staff/admin/requests" component={StaffPortalAdminRequests} />
      <Route path="/staff/admin/leave" component={StaffPortalAdminLeave} />
      <Route path="/staff/admin/shift-presets">{() => <StaffPortalConfiguration initialTab="shift-presets" />}</Route>
      <Route path="/staff/admin/shift-swaps" component={StaffPortalAdminShiftSwaps} />
      <Route path="/staff/admin/clock-in">{() => <StaffPortalConfiguration initialTab="clock-in" />}</Route>
      <Route path="/staff/admin/staff/:staffId" component={StaffPortalAdminDetail} />
      <Route path="/staff/admin/reports" component={StaffPortalAdminReports} />
      <Route path="/staff/admin/feature-toggles">{() => <StaffPortalSettings initialTab="features" />}</Route>
      <Route path="/staff/admin/integrations">{() => <StaffPortalSettings initialTab="integrations" />}</Route>
      <Route path="/staff/admin/roster-settings">{() => <StaffPortalConfiguration initialTab="roster" />}</Route>
      <Route path="/staff/admin/backup">{() => <StaffPortalSettings initialTab="backup" />}</Route>
      <Route path="/test/upload" component={TestUploadPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function ShiftCanvasRouter() {
  return (
    <Switch>
      <Route path="/" component={ShiftCanvasLanding} />
      <Route path="/onboarding" component={Onboarding} />
      <Route path="/faq" component={FAQ} />
      <Route path="/guides" component={Guides} />
      <Route path="/support" component={Support} />
      <Route path="/delete-account" component={DeleteAccount} />
      <Route path="/pricing" component={ShiftCanvasPricing} />
      <Route path="/about" component={ShiftCanvasAbout} />
      <Route path="/contact" component={ShiftCanvasContact} />
      <Route path="/features" component={ShiftCanvasFeatures} />
      <Route path="/privacy" component={ShiftCanvasPrivacy} />
      <Route path="/terms" component={ShiftCanvasTerms} />
      <Route path="/app/login" component={AppLogin} />
      <Route path="/app/sso-callback" component={AppSSOCallback} />
      <Route path="/app/oauth-complete" component={AppOAuthComplete} />
      <Route path="/admin" component={ProtectedDashboard} />
      <Route path="/app" component={ProtectedDashboard} />
      <Route path="/app/roster" component={ProtectedRoster} />
      <Route path="/app/my-roster" component={ProtectedMyRoster} />
      <Route path="/app/roster/create" component={ProtectedRosterCreate} />
      <Route path="/app/staff" component={ProtectedStaff} />
      <Route path="/app/availability" component={ProtectedAvailability} />
      <Route path="/app/requests" component={ProtectedRequests} />
      <Route path="/app/profile" component={ProtectedProfile} />
      <Route path="/app/notifications" component={ProtectedNotifications} />
      <Route path="/app/clock" component={ProtectedClock} />
      <Route path="/app/messages" component={ProtectedMessages} />
      <Route path="/app/feedback" component={ProtectedFeedback} />
      <Route path="/app/calendar" component={ProtectedCalendar} />
      <Route path="/app/meetings" component={ProtectedMeetings} />
      <Route path="/app/projects" component={ProtectedProjects} />
      <Route path="/app/project-task/:taskId" component={ProtectedProjectTask} />
      <Route path="/app/reminders" component={ProtectedReminders} />
      <Route path="/app/tasks" component={ProtectedTasks} />
      <Route path="/app/training" component={ProtectedTraining} />
      <Route path="/app/procedures" component={ProtectedProcedures} />
      <Route path="/clock/:token" component={AppClockIn} />
      <Route path="/join/:code" component={AppJoin} />
      <Route path="/staff" component={StaffPortalDashboard} />
      <Route path="/staff/dashboard" component={StaffPortalDashboard} />
      <Route path="/staff/roster" component={StaffPortalRoster} />
      <Route path="/staff/schedule" component={StaffPortalSchedule} />
      <Route path="/staff/clock" component={StaffPortalClock} />
      <Route path="/staff/messages" component={StaffPortalMessages} />
      <Route path="/staff/profile" component={StaffPortalProfile} />
      <Route path="/staff/settings">{() => <StaffPortalSettings />}</Route>
      <Route path="/staff/training" component={StaffPortalTraining} />
      <Route path="/staff/procedures" component={StaffPortalProcedures} />
      <Route path="/staff/tasks" component={StaffPortalTasks} />
      <Route path="/staff/projects" component={StaffPortalProjects} />
      <Route path="/staff/calendar" component={StaffPortalCalendar} />
      <Route path="/staff/meetings/:meetingId" component={StaffPortalMeetingView} />
      <Route path="/staff/meetings" component={StaffPortalMeetings} />
      <Route path="/staff/timesheet" component={StaffPortalTimesheet} />
      <Route path="/staff/admin/roster" component={StaffPortalAdminRoster} />
      <Route path="/staff/admin/staff" component={StaffPortalAdminStaff} />
      <Route path="/staff/admin/configuration">{() => <StaffPortalConfiguration />}</Route>
      <Route path="/staff/admin/settings">{() => <StaffPortalConfiguration initialTab="roles" />}</Route>
      <Route path="/staff/admin/roles">{() => <StaffPortalConfiguration initialTab="roles" />}</Route>
      <Route path="/staff/admin/availability" component={StaffPortalAdminAvailability} />
      <Route path="/staff/admin/requests" component={StaffPortalAdminRequests} />
      <Route path="/staff/admin/leave" component={StaffPortalAdminLeave} />
      <Route path="/staff/admin/shift-presets">{() => <StaffPortalConfiguration initialTab="shift-presets" />}</Route>
      <Route path="/staff/admin/shift-swaps" component={StaffPortalAdminShiftSwaps} />
      <Route path="/staff/admin/clock-in">{() => <StaffPortalConfiguration initialTab="clock-in" />}</Route>
      <Route path="/staff/admin/staff/:staffId" component={StaffPortalAdminDetail} />
      <Route path="/staff/admin/reports" component={StaffPortalAdminReports} />
      <Route path="/staff/admin/feature-toggles">{() => <StaffPortalSettings initialTab="features" />}</Route>
      <Route path="/staff/admin/integrations">{() => <StaffPortalSettings initialTab="integrations" />}</Route>
      <Route path="/staff/admin/roster-settings">{() => <StaffPortalConfiguration initialTab="roster" />}</Route>
      <Route path="/staff/admin/backup">{() => <StaffPortalSettings initialTab="backup" />}</Route>
      <Route path="/test/upload" component={TestUploadPage} />
      <Route component={NotFound} />
    </Switch>
  );
}

function MobileAppRouterContent() {
  return (
    <Switch>
      <Route path="/" component={AppDashboard} />
      {/* Server redirects authenticated users to /staff — catch it and show AppDashboard */}
      <Route path="/staff">{() => <AppDashboard />}</Route>
      <Route path="/app" component={AppDashboard} />
      <Route path="/app/roster" component={AppRoster} />
      <Route path="/app/my-roster" component={AppMyRoster} />
      <Route path="/app/roster/create" component={AppRosterCreate} />
      <Route path="/app/staff" component={AppStaff} />
      <Route path="/app/availability" component={AppAvailability} />
      <Route path="/app/requests" component={AppRequests} />
      <Route path="/app/profile" component={AppProfile} />
      <Route path="/app/notifications" component={AppNotifications} />
      <Route path="/app/clock" component={AppClock} />
      <Route path="/app/messages" component={AppMessages} />
      <Route path="/app/feedback" component={AppFeedback} />
      <Route path="/app/calendar" component={AppCalendar} />
      <Route path="/app/meetings" component={AppMeetings} />
      <Route path="/app/projects" component={AppProjects} />
      <Route path="/app/project-task/:taskId" component={AppProjectTask} />
      <Route path="/app/reminders" component={AppReminders} />
      <Route path="/app/tasks" component={AppTasks} />
      <Route path="/app/training" component={AppTraining} />
      <Route path="/app/procedures" component={AppProcedures} />
      {/* Needed on native too: App Store guideline 5.1.1(v) expects account
          deletion to be reachable in-app. Without this route the catch-all
          below silently rendered the dashboard instead. */}
      <Route path="/delete-account" component={DeleteAccount} />
      <Route component={AppDashboard} />
    </Switch>
  );
}

function MobileAppRouter() {
  const [location] = useLocation();
  
  // /onboarding renders standalone (outside the app shell): the zero-membership
  // gate sends users here, and inside the shell RequireSiteMembership would
  // render null — on native that used to be a blank screen with no way forward.
  const isUnauthRoute = location === "/app/login" || location === "/login" || location === "/app/sso-callback" || location.startsWith("/join/") || location.startsWith("/clock/") || location === "/test/upload" || location === "/app/oauth-complete" || location === "/onboarding";

  if (isUnauthRoute) {
    return (
      <Switch>
        <Route path="/app/login" component={NativeLoginPage} />
        <Route path="/login" component={NativeLoginPage} />
        <Route path="/app/sso-callback" component={AppSSOCallback} />
        <Route path="/app/oauth-complete" component={AppOAuthComplete} />
        <Route path="/join/:code" component={AppJoin} />
        <Route path="/clock/:token" component={AppClockIn} />
        <Route path="/test/upload" component={TestUploadPage} />
        <Route path="/onboarding" component={Onboarding} />
      </Switch>
    );
  }
  
  return (
    <ProtectedAppShell>
      <MobileAppRouterContent />
    </ProtectedAppShell>
  );
}

function BrandRouter() {
  const { isShiftCanvas, isNativeMobile, isLoading } = useBrand();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  if (isNativeMobile) {
    return <MobileAppRouter />;
  }

  // Hide chat widget on app pages and staff pages - it's only for onboarding/marketing pages
  const [location] = useLocation();
  const isAppPage = location.startsWith('/app') || location.startsWith('/staff');

  return (
    <>
      {isShiftCanvas ? <ShiftCanvasRouter /> : <CentretableRouter />}
      {!isAppPage && <AIChatWidget />}
    </>
  );
}

function PageSuspense({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<AuthTransitionLoader />}>
      {children}
    </Suspense>
  );
}

function App() {
  // Note: AuthProvider is already wrapped in main.tsx with proper devBypassMode/clerkAvailable props
  return (
    <ThemeProvider defaultTheme="light" storageKey="app-theme">
      <PersonalizationProvider>
        <QueryClientProvider client={queryClient}>
          <TooltipProvider>
            <BrandProvider>
              {isDev && <DevBar />}
              <Toaster />
              <PageSuspense>
                <BrandRouter />
              </PageSuspense>
            </BrandProvider>
          </TooltipProvider>
        </QueryClientProvider>
      </PersonalizationProvider>
    </ThemeProvider>
  );
}

export default App;
